import { useEffect, useMemo } from 'react';
import { Crown, Users } from 'lucide-react';
import { computeChannelPermissions, hasPermission, Permission, type Member, type PublicUser } from '@spawnwire/shared';
import { Avatar, ContextMenu, Spinner, UserBadge } from '../../components/ui';
import { loadMembers } from '../../lib/data';
import { NameText } from '../../lib/nameFonts';
import { subjectFor } from '../../lib/perms';
import { displayNameIn, roleColorOf, useStore } from '../../store/store';
import { ui } from '../../store/ui';
import { PanelHeader } from '../chat/PanelHeader';
import { useSpaceCommands } from '../chat/slashCommands';
import { TagChip } from './SpaceTag';
import { userMenu } from './userMenu';

function Row({ user, spaceId, ownerId, sub }: { user: PublicUser; spaceId: string | null; ownerId?: string | null; sub?: string }) {
  const status = useStore((s) => s.presences[user.id] ?? 'offline');
  const name = useStore((s) => displayNameIn(s, user.id, spaceId));
  const color = useStore((s) => roleColorOf(s, spaceId, user.id));
  const live = useStore((s) => s.users[user.id] ?? user);
  // The menu offers bots' person commands, which load after the first rows are drawn.
  useSpaceCommands(spaceId);
  return (
    <ContextMenu entries={userMenu(user.id, spaceId)}>
      <button
        type="button"
        onClick={(e) => ui.openProfile(user.id, spaceId, e.currentTarget)}
        className={`flex w-full items-center gap-3 rounded-md px-2 py-1.5 text-left hover:bg-hover ${status === 'offline' ? 'opacity-50 hover:opacity-100' : ''}`}
      >
        <Avatar user={live} size={32} status={status} />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1">
            <span className="truncate text-[15px] font-medium" style={color ? { color } : { color: 'var(--p-text)' }}>
              <NameText font={live.nameFont}>{name}</NameText>
            </span>
            <UserBadge user={live} className="ml-0" />
            {live.tag && <TagChip tag={live.tag} />}
            {ownerId === user.id && <Crown className="size-3.5 shrink-0 text-warning" aria-label="Owner" />}
          </span>
          {sub && <span className="block truncate text-xs text-muted">{sub}</span>}
        </span>
      </button>
    </ContextMenu>
  );
}

export function MemberList({ channelId }: { channelId: string }) {
  const channel = useStore((s) => s.channels[channelId]);
  const root = useStore((s) => (channel?.type === 'thread' && channel.parentId ? s.channels[channel.parentId] : channel));
  const spaceId = root?.spaceId ?? null;
  const space = useStore((s) => (spaceId ? s.spaces[spaceId] : undefined));
  const members = useStore((s) => (spaceId ? s.members[spaceId] : undefined));
  const loaded = useStore((s) => (spaceId ? !!s.membersLoaded[spaceId] : true));
  const presences = useStore((s) => s.presences);
  const category = useStore((s) => (root?.parentId ? s.channels[root.parentId] : undefined));

  useEffect(() => {
    if (spaceId) void loadMembers(spaceId).catch(() => {});
  }, [spaceId]);

  const groups = useMemo(() => {
    if (!space || !members || !root) return [];
    const state = useStore.getState();
    const roles = [...space.roles].sort((a, b) => b.position - a.position);
    const hoisted = roles.filter((r) => r.hoist && !r.isDefault);
    // Only list people who can see this channel.
    const visible = Object.values(members).filter((m) => {
      const subject = subjectFor(state, space.id, m.user.id);
      if (!subject) return false;
      const perms = computeChannelPermissions(subject, root.type === 'category' ? [] : (category?.overwrites ?? []), root.overwrites);
      return hasPermission(perms, Permission.VIEW_CHANNEL);
    });
    const byName = (a: Member, b: Member) => displayNameIn(state, a.user.id, space.id).localeCompare(displayNameIn(state, b.user.id, space.id));
    const out: { title: string; members: Member[] }[] = [];
    const placed = new Set<string>();
    for (const role of hoisted) {
      const list = visible.filter((m) => !placed.has(m.user.id) && (presences[m.user.id] ?? 'offline') !== 'offline' && m.roleIds.includes(role.id)).sort(byName);
      list.forEach((m) => placed.add(m.user.id));
      if (list.length) out.push({ title: role.name, members: list });
    }
    const online = visible.filter((m) => !placed.has(m.user.id) && (presences[m.user.id] ?? 'offline') !== 'offline').sort(byName);
    const offline = visible.filter((m) => !placed.has(m.user.id) && (presences[m.user.id] ?? 'offline') === 'offline').sort(byName);
    if (online.length) out.push({ title: 'Online', members: online });
    if (offline.length) out.push({ title: 'Offline', members: offline });
    return out;
  }, [space, members, presences, root, category]);

  if (channel && !channel.spaceId && channel.type !== 'thread') {
    const recipients = channel.recipients ?? [];
    return (
      <>
        <PanelHeader title={`Members — ${recipients.length}`} icon={<Users className="size-5 text-faint" aria-hidden />} />
        <div className="scroll-thin min-h-0 flex-1 overflow-y-auto p-2">
          {recipients.map((u) => (
            <Row key={u.id} user={u} spaceId={null} ownerId={channel.ownerId} />
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      <PanelHeader title="Members" icon={<Users className="size-5 text-faint" aria-hidden />} />
      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto p-2">
        {!loaded ? (
          <div className="flex justify-center p-6">
            <Spinner />
          </div>
        ) : (
          groups.map((g) => (
            <section key={g.title} className="mb-4">
              <h3 className="px-2 pb-1 text-xs font-semibold uppercase tracking-wide text-faint">
                {g.title} {'—'} {g.members.length}
              </h3>
              {g.members.map((m) => (
                <Row key={m.user.id} user={m.user} spaceId={spaceId} ownerId={space?.ownerId} />
              ))}
            </section>
          ))
        )}
      </div>
    </>
  );
}
